/**
 * useWorkspaceCoach Hook
 *
 * Provides context-aware AI coaching for the workspace.
 * Adapts guidance based on current mode, phase, and workspace state.
 */

import { useState, useCallback } from 'react'
import { useAIStore } from '@/stores/aiStore'
import { useAuthStore } from '@/stores/authStore'
import { useProjectStore } from '@/stores/projectStore'
import { getSessionToken, API_URL } from '@/lib/api/client'
import type { WorkspaceMode } from '@/stores/workspaceStore'

// ============================================================================
// TYPES
// ============================================================================

export interface SuggestedEntity {
  type: string
  label: string
  description: string
  confidence: number
}

export interface QuickAction {
  type: 'generate' | 'validate' | 'examples' | 'skip'
  label: string
  entityType?: string
  targetLabel?: string
}

export interface CoachMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  isStreaming?: boolean
  suggestions?: string[] // Auto-detected suggestions from bullet lists
  suggestedEntity?: SuggestedEntity // Entity suggestion from guided mode
  quickActions?: QuickAction[] // Quick action buttons from json:meta
}

export type AssistantMode = 'standard' | 'guided'

export interface FlowContext {
  phaseName: string
  phaseObjective?: string
  requiredEntities: string
  filledEntities: string
  missingEntities: string
}

export interface UseWorkspaceCoachReturn {
  messages: CoachMessage[]
  isLoading: boolean
  isStreaming: boolean
  error: string | null
  assistantMode: AssistantMode
  setAssistantMode: (mode: AssistantMode) => void
  sendMessage: (content: string, context: WorkspaceCoachContext) => Promise<void>
  clearMessages: () => void
  isConfigured: boolean
}

export interface WorkspaceCoachContext {
  mode: WorkspaceMode
  phaseId?: string
  phaseName?: string
  journeyMode: 'structured' | 'free'
  flowContext?: FlowContext
}

// ============================================================================
// HOOK
// ============================================================================

export function useWorkspaceCoach(): UseWorkspaceCoachReturn {
  const { apiKey, coachingModel } = useAIStore()
  const { currentAccount } = useAuthStore()
  const { currentProject } = useProjectStore()

  // State
  const [messages, setMessages] = useState<CoachMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [assistantMode, setAssistantMode] = useState<AssistantMode>('guided') // Default to guided for A/B testing

  const isConfigured = Boolean(apiKey && currentAccount && currentProject)

  // Send a message with workspace context
  const sendMessage = useCallback(
    async (content: string, context: WorkspaceCoachContext) => {
      if (!currentAccount || !currentProject || !apiKey || !content.trim()) {
        setError('Missing configuration or input')
        return
      }

      setError(null)
      setIsLoading(true)

      // Add user message
      const userMessage: CoachMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, userMessage])

      // Add placeholder assistant message
      const assistantMessageId = crypto.randomUUID()
      const assistantMessage: CoachMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      }
      setMessages((prev) => [...prev, assistantMessage])

      setIsStreaming(true)

      try {
        let streamedContent = ''
        let receivedQuickActions: QuickAction[] | undefined

        // Call workspace coaching endpoint
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        }
        const token = getSessionToken()
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }

        const response = await fetch(
          `${API_URL}/api/accounts/${currentAccount.id}/projects/${currentProject.id}/workspace/coach/chat/stream`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({
              message: content.trim(),
              mode: context.mode,
              assistantMode,
              phaseId: context.phaseId,
              phaseName: context.phaseName,
              journeyMode: context.journeyMode,
              flowContext: context.flowContext,
              apiKey,
              model: coachingModel,
            }),
          }
        )

        if (!response.ok) {
          throw new Error(`Workspace coach failed: ${response.statusText}`)
        }

        const reader = response.body?.getReader()
        if (!reader) throw new Error('No response body')

        const decoder = new TextDecoder()
        let buffer = ''
        let currentEventType = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.startsWith('event:')) {
              currentEventType = line.slice(6).trim()
              continue
            }
            if (line.startsWith('data:')) {
              try {
                const data = JSON.parse(line.slice(5).trim())

                switch (currentEventType) {
                  case 'text':
                    if (data.content) {
                      streamedContent += data.content
                      setMessages((prev) =>
                        prev.map((m) =>
                          m.id === assistantMessageId
                            ? { ...m, content: streamedContent }
                            : m
                        )
                      )
                    }
                    break

                  case 'quick_actions':
                    // Backend sends quick actions as a separate SSE event
                    if (data.actions && Array.isArray(data.actions)) {
                      receivedQuickActions = data.actions.filter((action: QuickAction) =>
                        action.type && action.label && ['generate', 'validate', 'examples', 'skip'].includes(action.type)
                      )
                    }
                    break

                  case 'error':
                    setError(data.message || 'Unknown error')
                    break
                }
              } catch {
                // Ignore parse errors
              }
            }
          }
        }

        // Finalize message and extract suggestions, entities, and quick actions
        const suggestions = extractSuggestions(streamedContent)
        const suggestedEntity = extractSuggestedEntity(streamedContent)
        // Use quick actions from SSE event if available, otherwise extract from content (fallback for standard mode)
        const quickActions = receivedQuickActions?.length ? receivedQuickActions : extractQuickActions(streamedContent)
        // Strip json:meta blocks from the visible content
        const cleanContent = stripMetaBlocks(streamedContent)
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId
              ? { ...m, content: cleanContent, isStreaming: false, suggestions, suggestedEntity, quickActions }
              : m
          )
        )
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message'
        setError(errorMessage)

        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId
              ? {
                  ...m,
                  content: `Sorry, something went wrong: ${errorMessage}`,
                  isStreaming: false,
                }
              : m
          )
        )
      } finally {
        setIsLoading(false)
        setIsStreaming(false)
      }
    },
    [currentAccount, currentProject, apiKey, coachingModel, assistantMode]
  )

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([])
    setError(null)
  }, [])

  return {
    messages,
    isLoading,
    isStreaming,
    error,
    assistantMode,
    setAssistantMode,
    sendMessage,
    clearMessages,
    isConfigured,
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extract suggestions from AI response
 * Looks for bullet lists (lines starting with - or •)
 */
function extractSuggestions(content: string): string[] {
  const lines = content.split('\n')
  const suggestions: string[] = []

  for (const line of lines) {
    const trimmed = line.trim()
    // Match lines starting with - or • followed by text
    if (trimmed.match(/^[-•]\s+(.+)/)) {
      const text = trimmed.replace(/^[-•]\s+/, '').trim()
      // Clean up any trailing punctuation or extra info in parentheses
      const cleaned = text.replace(/\([^)]*\)$/, '').replace(/[:;,.]$/, '').trim()
      if (cleaned.length > 0 && cleaned.length < 100) {
        suggestions.push(cleaned)
      }
    }
  }

  return suggestions
}

/**
 * Extract suggested entity from json:meta blocks in AI response
 */
function extractSuggestedEntity(content: string): SuggestedEntity | undefined {
  // Match ```json:meta blocks
  const metaBlockRegex = /```json:meta\s*([\s\S]*?)```/g
  const match = metaBlockRegex.exec(content)

  if (!match) return undefined

  try {
    const parsed = JSON.parse(match[1].trim())
    if (parsed.suggestedEntity && typeof parsed.suggestedEntity === 'object') {
      const entity = parsed.suggestedEntity
      if (entity.type && entity.label && entity.description) {
        return {
          type: String(entity.type),
          label: String(entity.label),
          description: String(entity.description),
          confidence: Number(entity.confidence) || 70,
        }
      }
    }
  } catch {
    // Parse error, ignore
  }

  return undefined
}

/**
 * Extract quick actions from json:meta blocks in AI response
 */
function extractQuickActions(content: string): QuickAction[] | undefined {
  // Match ```json:meta blocks
  const metaBlockRegex = /```json:meta\s*([\s\S]*?)```/g
  const match = metaBlockRegex.exec(content)

  if (!match) return undefined

  try {
    const parsed = JSON.parse(match[1].trim())
    if (parsed.quickActions && Array.isArray(parsed.quickActions)) {
      return parsed.quickActions.filter((action: any) =>
        action.type && action.label && ['generate', 'validate', 'examples', 'skip'].includes(action.type)
      )
    }
  } catch {
    // Parse error, ignore
  }

  return undefined
}

/**
 * Strip json:meta blocks from content for display
 * Handles both complete blocks (with closing ```) and incomplete/truncated blocks
 */
function stripMetaBlocks(content: string): string {
  // First strip complete blocks
  let stripped = content.replace(/```json:meta\s*[\s\S]*?```/g, '')
  // Also strip incomplete blocks at the end (no closing backticks)
  stripped = stripped.replace(/```json:meta\s*[\s\S]*$/g, '')
  return stripped.trim()
}

/**
 * Parse entity mentions in markdown format: [Label](#entity-uri)
 * Returns an array of text and entity mention objects
 */
export interface EntityMention {
  type: 'mention'
  entityId: string
  label: string
}

export type ParsedMessagePart = string | EntityMention

export function parseEntityMentions(content: string): ParsedMessagePart[] {
  const parts: ParsedMessagePart[] = []
  const regex = /\[([^\]]+)\]\(#([^)]+)\)/g
  let lastIndex = 0

  let match
  while ((match = regex.exec(content)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      const text = content.slice(lastIndex, match.index)
      if (text) parts.push(text)
    }

    // Add entity mention
    parts.push({
      type: 'mention',
      entityId: match[2], // URI from (#...)
      label: match[1],    // Label from [...]
    })

    lastIndex = regex.lastIndex
  }

  // Add remaining text
  if (lastIndex < content.length) {
    const text = content.slice(lastIndex)
    if (text) parts.push(text)
  }

  // If no mentions found, return original content as single string
  return parts.length === 0 ? [content] : parts
}
